import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useCVLanguage } from './i18n/language.component';
import { STORE_IMAGES } from './i18n/store-images';

// "Take a closer look": a row of close-ups of the Host, each with a caption
// under it, that scrolls sideways and runs off the edge of the page, in the
// manner of Apple's own product pages. The images are the App Store close-ups
// in the reader's language; the captions are in the strings files.

// The close-ups in the order the carousel shows them. A `wide` tile holds a
// feed or card whole; the others hold a sheet that runs off the bottom.
const CLOSE_UPS = [
    { key: 'pairing-code-modal' },
    { key: 'camera-and-microphone-feed', wide: true },
    { key: 'speakers-modal' },
    { key: 'captions-modal' },
    { key: 'this-device-feed', wide: true },
    { key: 'microphone-only-feed', wide: true },
    { key: 'speech-recognition-modal' },
    { key: 'help-guide-popup', wide: true },
    { key: 'session-log-modal' }
]

// how close to an edge still counts as being at it, in pixels
const SLACK = 4;

const Chevron = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M14.5 5.5L8 12l6.5 6.5" />
    </svg>
)

const CVCloserLook = () => {
    const { code, dir, t } = useCVLanguage();
    const strings = t.home.closer;
    const images = STORE_IMAGES[code].features;

    const track = useRef(null);
    const [edges, setEdges] = useState({ start: true, end: false });

    // scrollLeft runs from 0 into the negatives on a right-to-left page, so
    // only its size says how far along the row is
    const measure = useCallback(() => {
        const element = track.current;

        if (!element) {
            return
        }

        const offset = Math.abs(element.scrollLeft);
        const start = offset <= SLACK;
        const end = offset + element.clientWidth >= element.scrollWidth - SLACK;

        setEdges(current => (current.start === start && current.end === end ? current : { start, end }))
    }, [])

    // back to the first tile when the language, and so every image, changes
    useEffect(() => {
        if (track.current) {
            track.current.scrollLeft = 0;
        }

        measure();
        window.addEventListener('resize', measure);

        return () => window.removeEventListener('resize', measure)
    }, [measure, code])

    // Brings the next tile that starts past the left edge (or the last one
    // that starts before it) to the edge. Distances are measured from the
    // start side, which is the right on a right-to-left page.
    const step = forward => {
        const element = track.current;
        const box = element.getBoundingClientRect();
        const inset = parseFloat(getComputedStyle(element).scrollPaddingInlineStart) || 0;
        const rtl = dir === 'rtl';

        const distances = Array.from(element.children).map(tile => {
            const rect = tile.getBoundingClientRect();

            return rtl ? box.right - inset - rect.right : rect.left - box.left - inset
        })

        const distance = forward
            ? distances.find(value => value > SLACK)
            : distances.filter(value => value < -SLACK).pop();

        if (distance === undefined) {
            return
        }

        const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        element.scrollBy({ left: rtl ? -distance : distance, behavior: reduce ? 'auto' : 'smooth' });
    }

    return (
        <section className="cv-closer" id="closer">
            <div className="cv-page">
                <div className="cv-section-head">
                    <span className="cv-kicker"> {strings.kicker} </span>
                    <h2> {strings.title} </h2>
                </div>
            </div>

            {/* focusable, so the row can be scrolled from the keyboard */}
            <ul className="cv-closer-track" ref={track} onScroll={measure} tabIndex={0} aria-label={strings.label}>
                {CLOSE_UPS.map(({ key, wide }) => {
                    const image = images[key];
                    const item = strings.items[key];

                    return (
                        <li key={key} className={`cv-closer-item${wide ? ' is-wide' : ''}`}>
                            <figure>
                                <div className="cv-closer-frame">
                                    <img src={image.src} width={image.width} height={image.height} alt={item.alt} loading="lazy" />
                                </div>
                                <figcaption>
                                    <strong> {item.title} </strong> {item.body}
                                </figcaption>
                            </figure>
                        </li>
                    )
                })}
            </ul>

            <div className="cv-page cv-closer-controls">
                <button type="button" aria-label={strings.previous} disabled={edges.start} onClick={() => step(false)}>
                    <Chevron />
                </button>
                <button type="button" className="cv-closer-next" aria-label={strings.next} disabled={edges.end} onClick={() => step(true)}>
                    <Chevron />
                </button>
            </div>
        </section>
    )
}

export default CVCloserLook;
